import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Users, Trash2, Plus, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { API } from '../../App';
import HamburgerMenu from '../../components/HamburgerMenu';

export default function TeamDetailView() {
  const { orgId, teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgMembers, setOrgMembers] = useState([]);
  const [addAthlete, setAddAthlete] = useState('');
  const [addCoach, setAddCoach] = useState('');
  const [coachPerm, setCoachPerm] = useState('view');
  const [professionalRole, setProfessionalRole] = useState('coach');

  const load = useCallback(() => {
    axios.get(`${API}/orgs/${orgId}/teams/${teamId}`)
      .then((res) => setTeam(res.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load team.'))
      .finally(() => setLoading(false));
  }, [orgId, teamId]);

  useEffect(() => {
    load();
    axios.get(`${API}/orgs/${orgId}/members`).then((res) => setOrgMembers(res.data.members || [])).catch(() => {});
  }, [load, orgId]);

  const handleAddAthlete = async () => {
    if (!addAthlete) return;
    try {
      await axios.post(`${API}/orgs/${orgId}/teams/${teamId}/athletes`, { athlete_user_id: addAthlete });
      toast.success('Athlete added.');
      setAddAthlete('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add athlete.'); }
  };

  const handleRemoveAthlete = async (uid) => {
    try {
      await axios.delete(`${API}/orgs/${orgId}/teams/${teamId}/athletes/${uid}`);
      toast.success('Athlete removed from team.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove.'); }
  };

  const handleAssignCoach = async () => {
    if (!addCoach) return;
    try {
      const selected = orgMembers.find((m) => m.user_id === addCoach);
      await axios.post(`${API}/orgs/${orgId}/teams/${teamId}/coaches`, {
        coach_user_id: addCoach, permission_level: coachPerm,
        ...(selected?.role === 'org_admin' ? { professional_role: professionalRole } : {}),
      });
      toast.success('Staff member assigned.');
      setAddCoach('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign coach.'); }
  };

  const handleUnassignCoach = async (uid) => {
    try {
      await axios.delete(`${API}/orgs/${orgId}/teams/${teamId}/coaches/${uid}`);
      toast.success('Staff member unassigned.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unassign.'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900">
        <HamburgerMenu />
        <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>
      </div>
    );
  }
  if (!team) return null;

  const isAdmin = team.my_role === 'org_admin';
  const athleteIdsOnTeam = new Set((team.athletes || []).map((a) => a.user_id));
  const availableAthletes = orgMembers.filter((m) => m.role === 'athlete' && !athleteIdsOnTeam.has(m.user_id));
  const coachIdsOnTeam = new Set((team.coaches || []).map((c) => c.user_id));
  const availableStaff = orgMembers.filter(
    (m) => [
      'org_admin',
      'coach',
      'assistant_coach',
      'nutritionist',
      'healthcare_professional',
    ].includes(m.role)
      && !coachIdsOnTeam.has(m.user_id)
  );


  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900" data-testid="team-detail-page">
      <HamburgerMenu />
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <Button variant="outline" onClick={() => navigate('/teams')} className="glass border-stone-700 text-stone-300 hover:bg-stone-800 mb-5" data-testid="team-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Button>

        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-amber-400 mb-1">{team.sport_or_group || 'Team'}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-wider">{team.name}</h1>
        </div>

        {/* Athletes */}
        <div className="mb-8">
          <h2 className="text-sm font-bold text-white tracking-wider uppercase mb-2 flex items-center gap-2">
            <Users className="w-4 h-4 text-amber-400" />Athletes ({(team.athletes || []).length})
          </h2>
          {team.can_manage && (
            <div className="flex gap-2 mb-3">
              <Select value={addAthlete} onValueChange={setAddAthlete}>
                <SelectTrigger className="bg-stone-900 border-stone-700 text-white flex-1" data-testid="add-athlete-select">
                  <SelectValue placeholder="Add an athlete from your organization…" />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700 text-white">
                  {availableAthletes.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-stone-500">No unassigned athletes — invite more first.</div>
                  ) : availableAthletes.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id} className="focus:bg-stone-800 focus:text-white">{m.username} ({m.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleAddAthlete} disabled={!addAthlete} className="bg-amber-700 hover:bg-amber-600 text-white font-bold" data-testid="add-athlete-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
          {(team.athletes || []).length === 0 ? (
            <Card className="glass border-stone-800 p-5 text-center"><p className="text-stone-400 text-sm">No athletes on this team yet.</p></Card>
          ) : (
            <div className="space-y-1.5">
              {team.athletes.map((a) => (
                <Card key={a.user_id} className="glass border-stone-800 px-4 py-2.5 flex items-center gap-3" data-testid={`team-athlete-${a.user_id}`}>
                  <button type="button" className="text-left flex-1" onClick={() => navigate(`/teams/athlete/${orgId}/${a.user_id}`)}>
                    <span className="text-white font-medium hover:text-amber-400 transition-colors">{a.username}</span>
                    <p className="text-[11px] text-stone-500">{a.email}</p>
                  </button>
                  {team.can_manage && (
                    <button type="button" onClick={() => handleRemoveAthlete(a.user_id)} className="text-stone-500 hover:text-blood-500 p-1" aria-label="Remove from team" data-testid={`remove-team-athlete-${a.user_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Coaches */}
        <div>
          <h2 className="text-sm font-bold text-white tracking-wider uppercase mb-2 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-400" />Assigned Staff ({(team.coaches || []).length})
          </h2>
          {isAdmin && (
            <div className="flex flex-wrap gap-2 mb-3">
              <Select value={addCoach} onValueChange={setAddCoach}>
                <SelectTrigger className="bg-stone-900 border-stone-700 text-white flex-1 min-w-[180px]" data-testid="assign-coach-select">
                  <SelectValue placeholder="Assign a coach / staff member…" />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700 text-white">
                  {availableStaff.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-stone-500">No unassigned staff — invite coaches first.</div>
                  ) : availableStaff.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id} className="focus:bg-stone-800 focus:text-white">{m.username} ({m.role.replace('_', ' ')})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {orgMembers.find((m) => m.user_id === addCoach)?.role === 'org_admin' && (
                <Select value={professionalRole} onValueChange={setProfessionalRole}>
                  <SelectTrigger className="bg-stone-900 border-stone-700 text-white w-48" data-testid="professional-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-white">
                    {['coach', 'assistant_coach', 'nutritionist', 'healthcare_professional'].map((role) => (
                      <SelectItem key={role} value={role}>{role.replaceAll('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={coachPerm} onValueChange={setCoachPerm}>
                <SelectTrigger className="bg-stone-900 border-stone-700 text-white w-32" data-testid="coach-perm-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-700 text-white">
                  <SelectItem value="view" className="focus:bg-stone-800 focus:text-white">View</SelectItem>
                  <SelectItem value="manage" className="focus:bg-stone-800 focus:text-white">Manage</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleAssignCoach} disabled={!addCoach} className="bg-amber-700 hover:bg-amber-600 text-white font-bold" data-testid="assign-coach-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
          {(team.coaches || []).length === 0 ? (
            <Card className="glass border-stone-800 p-5 text-center"><p className="text-stone-400 text-sm">No staff assigned to this team yet.</p></Card>
          ) : (
            <div className="space-y-1.5">
              {team.coaches.map((c) => (
                <Card key={c.user_id} className="glass border-stone-800 px-4 py-2.5 flex items-center gap-3" data-testid={`team-coach-${c.user_id}`}>
                  <div className="flex-1">
                    <span className="text-white font-medium">{c.username}</span>
                    <p className="text-[11px] text-stone-500">{(c.professional_role || c.role || '').replaceAll('_', ' ')} · {c.permission_level} access</p>
                  </div>
                  {isAdmin && (
                    <button type="button" onClick={() => handleUnassignCoach(c.user_id)} className="text-stone-500 hover:text-blood-500 p-1" aria-label="Unassign" data-testid={`unassign-coach-${c.user_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
